import { BACKEND_KEYS } from '../../common/consts/app-keys.const';
import { HttpFactoryService, HttpService } from '../../common/services';

export interface IParticipant {
  id: number;
  name: string;
  position: number;
  status: string;
  userId?: number | null;
}

export interface IMessage {
  id: number;
  sender: string;
  text: string;
  createdAt: string;
  edited?: boolean;
  replyToId?: number | null;
  replyTo?: { id: number; sender: string; text: string } | null;
}

export interface IReviewPoll {
  id: number;
  title: string;
  meetingUrl: string | null;
  meetingLinkLabel: string | null;
  isActive: boolean;
  adminId: number;
  hasPassword: boolean;
  meetingStarted: boolean;
  teamId: number | null;
  team: { id: number; name: string } | null;
  teams: { id: number; name: string }[];
  participants: IParticipant[];
  adminNames?: string[];
  hasDiscordWebhook?: boolean;
  discordChannelUrl?: string | null;
  hasSlackWebhook?: boolean;
  slackChannelUrl?: string | null;
  companyId?: number | null;
  featureToggles?: Record<string, boolean> | null;
  company?: { slug: string; name: string; logoUrl: string | null; primaryColor: string | null } | null;
  waitEstimate?: { avgMinutes: number | null; estimates: { participantId: number; estimatedMinutes: number }[] } | null;
}

class ReviewService {
  httpService: HttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createHttpService();
  }

  public async createReview(title: string, meetingUrl?: string, teamIds?: number[], password?: string, meetingLinkLabel?: string) {
    return this.httpService.post<
      { id: number; slug: string; title: string; meetingUrl: string | null; teamIds: number[] },
      { title: string; meetingUrl?: string; teamIds?: number[]; password?: string; meetingLinkLabel?: string }
    >(BACKEND_KEYS.REVIEW, {
      title,
      ...(meetingUrl ? { meetingUrl } : {}),
      ...(teamIds && teamIds.length > 0 ? { teamIds } : {}),
      ...(password ? { password } : {}),
      ...(meetingLinkLabel ? { meetingLinkLabel } : {})
    });
  }

  public async updateReviewTitle(slug: string, title: string) {
    return this.httpService.put<{ title: string }, { title: string }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/title`,
      { title }
    );
  }

  public async updateMeetingUrl(slug: string, meetingUrl?: string) {
    return this.httpService.put<
      { meetingUrl: string | null },
      { meetingUrl?: string }
    >(`${BACKEND_KEYS.REVIEW}/${slug}/meeting-url`, {
      meetingUrl
    });
  }

  public async updateMeetingLinkLabel(slug: string, label?: string) {
    return this.httpService.put<
      { meetingLinkLabel: string | null },
      { label?: string }
    >(`${BACKEND_KEYS.REVIEW}/${slug}/meeting-link-label`, {
      label
    });
  }

  public async getReview(slug: string) {
    return this.httpService.get<any>(`${BACKEND_KEYS.REVIEW}/${slug}`);
  }

  public async sendInvites(slug: string, emails: string[]) {
    return this.httpService.post<{ sent: number }, { emails: string[] }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/send-invites`,
      { emails }
    );
  }

  public async getInvites(slug: string) {
    return this.httpService.get<{ email: string; joinedAt: string | null; createdAt: string }[]>(
      `${BACKEND_KEYS.REVIEW}/${slug}/invites`
    );
  }

  public async pollReview(slug: string) {
    return this.httpService.get<IReviewPoll>(`${BACKEND_KEYS.REVIEW}/${slug}/poll`);
  }

  public async joinReview(slug: string, name: string, password?: string) {
    return this.httpService.post<IParticipant & { token: string; checkinToken?: string | null }, { name: string; password?: string }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/join`,
      { name, ...(password ? { password } : {}) }
    );
  }

  public async updateReviewPassword(slug: string, password?: string) {
    return this.httpService.put<{ hasPassword: boolean }, { password?: string }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/password`,
      { password }
    );
  }

  public async getReviewPassword(slug: string) {
    return this.httpService.get<{ password: string | null }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/password`
    );
  }

  public async getAdminReviews() {
    return this.httpService.get<any[]>(`${BACKEND_KEYS.REVIEW}/admin/list`);
  }

  public async getMyParticipatingReviews() {
    return this.httpService.get<any[]>(`${BACKEND_KEYS.REVIEW}/my/participating`);
  }

  public async getExpiringReviews() {
    return this.httpService.get<
      { id: number; slug: string; title: string; expiresAt: string; extendedOnce: boolean }[]
    >(`${BACKEND_KEYS.REVIEW}/my/expiring`);
  }

  public async extendReview(slug: string) {
    return this.httpService.put<{ expiresAt: string }, object>(
      `${BACKEND_KEYS.REVIEW}/${slug}/extend`,
      {}
    );
  }

  public async toggleMeetingStarted(slug: string) {
    return this.httpService.put<{ meetingStarted: boolean }, object>(
      `${BACKEND_KEYS.REVIEW}/${slug}/toggle-meeting`,
      {}
    );
  }

  public async getTeamMembersForReview(slug: string) {
    return this.httpService.get<{ email: string; displayName: string | null }[]>(
      `${BACKEND_KEYS.REVIEW}/${slug}/team-members`
    );
  }

  public async linkParticipant(slug: string, participantId: number) {
    return this.httpService.put<void, object>(
      `${BACKEND_KEYS.REVIEW}/${slug}/participant/${participantId}/link`,
      {}
    );
  }

  public async isUserAdmin(slug: string) {
    return this.httpService.get<boolean>(`${BACKEND_KEYS.REVIEW}/${slug}/is-admin`);
  }

  public async getReviewAdmins(slug: string) {
    return this.httpService.get<{
      ownerId: number;
      coAdmins: { id: number; userId: number; email: string; createdAt: string }[];
    }>(`${BACKEND_KEYS.REVIEW}/${slug}/admins`);
  }

  public async checkCoAdminEmail(slug: string, email: string) {
    return this.httpService.post<
      { exists: boolean; email: string; alreadyAdmin: boolean; isOwner: boolean; alreadyInvited: boolean },
      { email: string }
    >(`${BACKEND_KEYS.REVIEW}/${slug}/admins/check`, { email });
  }

  public async addCoAdmin(slug: string, email: string) {
    return this.httpService.post<
      { id: number; userId: number; email: string },
      { email: string }
    >(`${BACKEND_KEYS.REVIEW}/${slug}/admins`, { email });
  }

  public async inviteCoAdmin(slug: string, email: string) {
    return this.httpService.post<
      { success: boolean },
      { email: string }
    >(`${BACKEND_KEYS.REVIEW}/${slug}/admins/invite`, { email });
  }

  public async getPendingCoAdminInvites(slug: string) {
    return this.httpService.get<
      { id: number; email: string; createdAt: string }[]
    >(`${BACKEND_KEYS.REVIEW}/${slug}/admins/pending`);
  }

  public async resendCoAdminInvite(slug: string, inviteId: number) {
    return this.httpService.post<{ success: boolean }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/admins/pending/${inviteId}/resend`
    );
  }

  public async revokeCoAdminInvite(slug: string, inviteId: number) {
    return this.httpService.delete<{ success: boolean }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/admins/pending/${inviteId}`
    );
  }

  public async removeCoAdmin(slug: string, coAdminId: number) {
    return this.httpService.delete<{ success: boolean }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/admins/${coAdminId}`
    );
  }

  public async getReviewTeams(slug: string) {
    return this.httpService.get<{ id: number; name: string }[]>(
      `${BACKEND_KEYS.REVIEW}/${slug}/teams`
    );
  }

  public async addTeamToReview(slug: string, teamId: number) {
    return this.httpService.post<{ success: boolean }, { teamId: number }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/teams`,
      { teamId }
    );
  }

  public async removeTeamFromReview(slug: string, teamId: number) {
    return this.httpService.delete<{ success: boolean }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/teams/${teamId}`
    );
  }

  public async deleteReview(slug: string) {
    return this.httpService.delete<{ success: boolean }>(
      `${BACKEND_KEYS.REVIEW}/${slug}`
    );
  }

  public async reorderParticipants(slug: string, order: number[]) {
    return this.httpService.put<IReviewPoll, { order: number[] }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/reorder`,
      { order }
    );
  }

  public async setParticipantStatus(
    slug: string,
    participantId: number,
    status: string
  ) {
    return this.httpService.put<IReviewPoll, { status: string }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/participant/${participantId}/status`,
      { status }
    );
  }

  public async removeParticipant(slug: string, participantId: number) {
    return this.httpService.delete<IReviewPoll>(
      `${BACKEND_KEYS.REVIEW}/${slug}/participant/${participantId}`
    );
  }

  public async renameParticipant(slug: string, participantId: number, name: string) {
    return this.httpService.put<{ success: boolean; name: string }, { name: string }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/participant/${participantId}/rename`,
      { name }
    );
  }

  public async clearCompleted(slug: string) {
    return this.httpService.delete<IReviewPoll>(
      `${BACKEND_KEYS.REVIEW}/${slug}/participants/completed`
    );
  }

  public async reinstateAll(slug: string) {
    return this.httpService.post<IReviewPoll, {}>(
      `${BACKEND_KEYS.REVIEW}/${slug}/participants/reinstate`,
      {}
    );
  }

  public async leaveReview(slug: string, participantId: number) {
    return this.httpService.post<{ success: boolean }, { participantId: number }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/leave`,
      { participantId }
    );
  }

  public async getMessages(slug: string, since?: string) {
    const url = since
      ? `${BACKEND_KEYS.REVIEW}/${slug}/messages?since=${encodeURIComponent(since)}`
      : `${BACKEND_KEYS.REVIEW}/${slug}/messages`;
    return this.httpService.get<IMessage[]>(url);
  }

  public async sendMessage(slug: string, sender: string, text: string, replyToId?: number) {
    return this.httpService.post<IMessage, { sender: string; text: string; replyToId?: number }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/messages`,
      { sender, text, ...(replyToId ? { replyToId } : {}) }
    );
  }

  public async editMessage(slug: string, messageId: number, sender: string, text: string) {
    return this.httpService.put<IMessage, { sender: string; text: string }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/messages/${messageId}`,
      { sender, text }
    );
  }

  public async getDiscordWebhook(slug: string) {
    return this.httpService.get<{ webhookUrl: string | null }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/discord-webhook`
    );
  }

  public async setDiscordWebhook(slug: string, webhookUrl: string | null) {
    return this.httpService.put<{ success: boolean }, { webhookUrl: string | null }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/discord-webhook`,
      { webhookUrl }
    );
  }

  public async getSlackWebhook(slug: string) {
    return this.httpService.get<{ webhookUrl: string | null; channelUrl: string | null }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/slack-webhook`
    );
  }

  public async setSlackWebhook(slug: string, webhookUrl: string | null, channelUrl?: string | null) {
    return this.httpService.put<{ success: boolean }, { webhookUrl: string | null; channelUrl?: string | null }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/slack-webhook`,
      { webhookUrl, channelUrl }
    );
  }

  public async updateFeatureToggles(slug: string, toggles: Record<string, boolean>) {
    return this.httpService.put<{ featureToggles: Record<string, boolean> }, { toggles: Record<string, boolean> }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/feature-toggles`,
      { toggles }
    );
  }

  public async saveAsTemplate(slug: string, name: string) {
    return this.httpService.post<{ id: number; name: string }, { name: string }>(
      `${BACKEND_KEYS.REVIEW}/${slug}/save-as-template`,
      { name }
    );
  }
}

const reviewService = new ReviewService(new HttpFactoryService());
export { reviewService };
